<template>
  <div id="app">
    <loaderOverlay v-show="syncing" />
		<errorOverlay v-model="error" />
    <appHeader />
    <appSidemenu v-show="sidemenu" v-if="hasUser" />
    <notifications
      group="messages"
      position="bottom center"
    />
    <router-view />
    <div class="only-landscape">{{res.nl.onlyLandscape}}</div>
  </div>
</template>

<style lang="scss">
@import url("https://fonts.googleapis.com/css?family=Roboto:400,700,900");
* {
  box-sizing: border-box;
}
body {
  padding: 0;
  margin: 0;
  font-family: "Roboto", sans-serif;
  font-size: $fs;
  color: $text;
  overflow-y: hidden; // disable pull to refresh
}
#app {
  height: 100vh;
  width: 100vw;
  display: flex;
  flex-direction: column;
  align-items: stretch;
}
*:active,
*:focus {
  -webkit-tap-highlight-color: rgba(255, 255, 255, 0);
  outline: none;
}
.only-landscape {
  display: none;
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: center;
  font-size: $h1;
}
@media (orientation: portrait) {
  #app {
    > * {
      display: none;
    }
    .only-landscape {
      display: flex;
    }
  }
}
</style>

<script>
import { sync } from 'vuex-router-sync'
import { store } from './store'
import { applicationUserManager } from './applicationUserManager'

import { EventBus } from './event-bus.js'

import appHeader from './components/app-header'
import appSidemenu from './components/app-sidemenu'
import loaderOverlay from './components/loaderOverlay'
import errorOverlay from './components/app-error'

export default {
	name: 'app',
	store: store,
	components: {
		appHeader,
		appSidemenu,
		loaderOverlay,
		errorOverlay
	},
	data () {
		return {
			error: null,
			res: {
				nl: {
					onlyLandscape: 'Draai de tablet om de app te gebruiken.'
				},
				fr: {
					onlyLandscape: 'Faites pivoter la tablette pour utiliser l\'application.'
				}
			}
		}
	},
	computed: {
		syncing () {
			return this.$store.getters.syncing
		},
		sidemenu () {
			return this.$store.state.app.sidemenu
		},
		getUser(){
			return this.$store.getters.user
		},
		hasUser(){
			let hasUser = typeof this.getUser !== 'undefined' && this.getUser !== null && Object.keys(this.getUser).length > 0
			return hasUser
		}
	},
	watch: {
		$route () {
			this.$store.dispatch('toggleSidemenu', 'close')
		}
	},
	created () {
		this.$store.commit('setLoaded', false) // reset localy stored loaded state
		sync(store, this.$router)

		// When user gets logged in the app checks the if the login is correct.
		// Everything gets loaded once everything is ok, otherwise the app redirects to the login page.
		var route = this.$route.name
		if (route !== 'callback') {

			this.checkLogin().then(async()=>{
				await this.getClients()
				await this.getProducts()
				await this.getOrders()
			})
		}

		EventBus.$on('error', error =>{
			console.error(JSON.stringify(error, Object.getOwnPropertyNames(error)))
			this.$set(this, 'error', error)
		})
	},
	methods: {
		 checkLogin(){
			return new Promise(async (resolve)=>{
				this.setLoadingStatus(true)
				let user = await applicationUserManager.getUser() || null

				if(!user){
					user = await applicationUserManager.login()
				}

				this.$store.commit('updateUser', user)
				this.setLoadingStatus(false)
				resolve()

			}).catch(err=>{
				EventBus.$emit('error', err)
			})
		},
		getProducts(){
			this.$store
				.dispatch('getProducts', this.getUser)
				.then(data => {
					if (
						data.products.length ||
            data.brands.length ||
            data.subBrands.length ||
            data.categories.length ||
            data.subCategories.length ||
            data.subSubCategories.length ||
            data.subSubSubCategories.length
					) {
						store.dispatch('updateProducts', data).then(() => {
							window.localStorage.setItem(
								'lastUpdateProducts',
								data.info.timestamp
							)
							store.dispatch('setProductsInState')
							EventBus.$emit('productsLoaded')
						}).catch(e=>{
							console.error(e)
							EventBus.$emit('error', e)
						})
					} else {
						store.dispatch('setProductsInState')
						EventBus.$emit('productsLoaded')
					}
				})
				.catch(e => {
					// no connection / error -> get from local db
					if (e.response && e.response.status === 403) {
						// not logged in
						applicationUserManager.login()
					} else {
						console.error(e)
						EventBus.$emit('error', e)
					}
				})
		},
		getClients(){
			const $this = this
			$this.setLoadingStatus(true)

			// Load external client data
			this.$store
				.dispatch('getClients', this.getUser)
				.then(async data => {
					// If there are new clients, merge with indexed clients
					store.dispatch('updateClients', { clients: [...data.clients], init: true }).then((response)=>{
						window.localStorage.setItem(
							'lastUpdateClients',
							data.info.timestamp
						)
						$this.setLoadingStatus(false)
						EventBus.$emit('clientsLoaded')
					}).catch(e=>{
						console.error('updateClients failed:', e)
					})
				})
				.catch(e => {
					$this.setLoadingStatus(false)
					if (e.response && e.response.status === 403) {
						// not logged in
						applicationUserManager.login()
					} else {
						console.error(e)
						EventBus.$emit('error', e)
					}
					// no connection / error -> get from local db
					store.dispatch('updateClients', { clients: [], init: true }).then(()=>{
						$this.setLoadingStatus(false)
						EventBus.$emit('clientsLoaded')
					}).catch(e=>{
						console.error('updateClients failed:', e)
						EventBus.$emit('error', e)
					})
				})
		},
		async getClientsFromIndexedDB(){
			this.setLoadingStatus(true)
			await store.dispatch('getClientsFromIndexedDb')
			this.setLoadingStatus(false)
		},
		getOrders(){
			const $this = this
			this.$store.dispatch('getOrders').then(e => {
				$this.setLoadingStatus(false)
				EventBus.$emit('ordersLoaded')
			})
		},
		setLoadingStatus(status){
			this.$store.commit('sync', status)
		}
	}
}
</script>
